// src/pages/locations/LocationManagementPage.jsx - Absolute Maximum Compactness
import React, { useState, useEffect } from 'react';
import { getDestinations, createDestination, updateDestination, deleteDestination, createSubDestination, updateSubDestination, deleteSubDestination, getSubDestinationsByDestination } from '../../services/locationsService';
import Loader from '../../components/common/Loader';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import RichTextEditor from '../../components/forms/RichTextEditor'; 
import FileUploader from '../../components/forms/FileUploader'; 
import { slugify } from '../../utils/helpers';
import { PlusCircleIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../contexts/ToastContext';

function LocationManagementPage() {
  const { showSuccess, showError, showWarning, showInfo } = useToast();
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddDestinationForm, setShowAddDestinationForm] = useState(false);
  const [destinationForm, setDestinationForm] = useState({
    id: '', name: '', slug: '', description: '', meta_title: '', meta_description: '', is_active: true, display_order: 0
  });

  const [subDestinationForm, setSubDestinationForm] = useState({
    id: '', name: '', slug: '', description: '', is_active: true, display_order: 0, destination_id: null
  });
  const [editingSubDestinationId, setEditingSubDestinationId] = useState(null);
  const [showSubDestinationFormForDest, setShowSubDestinationFormForDest] = useState(null);
  
  // Validation states
  const [destinationValidationErrors, setDestinationValidationErrors] = useState({});
  const [subDestinationValidationErrors, setSubDestinationValidationErrors] = useState({});
  const [isDestinationFormValid, setIsDestinationFormValid] = useState(false);
  const [isSubDestinationFormValid, setIsSubDestinationFormValid] = useState(false);

  // Caching constants and state
  const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
  const DESTINATIONS_CACHE_KEY = 'locations_management_cache';
  const [cacheStatus, setCacheStatus] = useState('loading');
  const [isFetching, setIsFetching] = useState(false);

  // Caching utility functions
  const getCachedData = () => {
    try {
      const cached = localStorage.getItem(DESTINATIONS_CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          return data;
        }
        localStorage.removeItem(DESTINATIONS_CACHE_KEY);
      }
    } catch (error) {
      console.warn('Failed to read destinations cache:', error);
      localStorage.removeItem(DESTINATIONS_CACHE_KEY);
    }
    return null;
  };

  const setCachedData = (data) => {
    try {
      localStorage.setItem(DESTINATIONS_CACHE_KEY, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.warn('Failed to cache destinations:', error);
    }
  };

  const clearDestinationsCache = () => {
    localStorage.removeItem(DESTINATIONS_CACHE_KEY);
    showInfo('Destinations cache cleared - fresh data will be loaded');
  };

  // Cache status indicator component
  const CacheStatusIndicator = ({ status, itemCount }) => {
    const getStatusConfig = () => {
      switch (status) {
        case 'cached':
          return {
            color: 'text-green-600 bg-green-100 border-green-200',
            icon: '💾',
            text: `Cached (${itemCount} destinations)`
          };
        case 'fresh':
          return {
            color: 'text-blue-600 bg-blue-100 border-blue-200',
            icon: '🔄',
            text: `Fresh (${itemCount} destinations)`
          };
        case 'loading':
          return {
            color: 'text-gray-600 bg-gray-100 border-gray-200',
            icon: '⏳',
            text: 'Loading destinations...'
          };
        case 'error':
          return {
            color: 'text-red-600 bg-red-100 border-red-200',
            icon: '❌',
            text: 'Error loading destinations'
          };
        default:
          return {
            color: 'text-gray-600 bg-gray-100 border-gray-200',
            icon: '❓',
            text: 'Unknown status'
          };
      }
    };

    const config = getStatusConfig();
    
    return (
      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        <span className="mr-1">{config.icon}</span>
        <span>{config.text}</span>
      </div>
    );
  };

  // Optimized smart update functions
  const updateDestinationInCache = (destinationData) => {
    setDestinations(prev => {
      const updated = prev.map(dest => 
        dest.id === destinationData.id 
          ? { ...dest, ...destinationData }
          : dest
      );
      setCachedData(updated);
      return updated;
    });
  };

  const addDestinationToCache = (newDestination) => {
    const destinationWithSubs = {
      ...newDestination,
      sub_destinations: []
    };
    setDestinations(prev => {
      const updated = [...prev, destinationWithSubs];
      setCachedData(updated);
      return updated;
    });
  };

  const removeDestinationFromCache = (destinationId) => {
    setDestinations(prev => {
      const updated = prev.filter(dest => dest.id !== destinationId);
      setCachedData(updated);
      return updated;
    });
  };

  const updateSubDestinationInCache = (destinationId, subDestData) => {
    setDestinations(prev => {
      const updated = prev.map(dest => {
        if (dest.id === destinationId) {
          const updatedSubs = dest.sub_destinations.map(sub =>
            sub.id === subDestData.id ? { ...sub, ...subDestData } : sub
          );
          return { ...dest, sub_destinations: updatedSubs };
        }
        return dest;
      });
      setCachedData(updated);
      return updated;
    });
  };

  const addSubDestinationToCache = (destinationId, newSubDestination) => {
    setDestinations(prev => {
      const updated = prev.map(dest => {
        if (dest.id === destinationId) {
          return {
            ...dest,
            sub_destinations: [...(dest.sub_destinations || []), newSubDestination]
          };
        }
        return dest;
      });
      setCachedData(updated);
      return updated;
    });
  };

  const removeSubDestinationFromCache = (subDestinationId) => {
    setDestinations(prev => {
      const updated = prev.map(dest => ({
        ...dest,
        sub_destinations: dest.sub_destinations.filter(sub => sub.id !== subDestinationId)
      }));
      setCachedData(updated);
      return updated;
    });
  };

  // Manual refresh function
  const refreshDestinations = async () => {
    clearDestinationsCache();
    setCacheStatus('loading');
    await fetchDestinations();
    showSuccess('Destinations refreshed successfully');
  };

  // Validation functions
  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'name':
        if (!value || !value.trim()) {
          error = 'Name is required';
        } else if (value.trim().length < 2) {
          error = 'Name must be at least 2 characters';
        } else if (value.trim().length > 100) {
          error = 'Name must be less than 100 characters';
        }
        break;
        
      case 'slug':
        if (!value || !value.trim()) {
          error = 'Slug is required';
        } else if (value.trim().length < 2) {
          error = 'Slug must be at least 2 characters';
        } else if (!/^[a-z0-9-]+$/.test(value)) {
          error = 'Slug can only contain lowercase letters, numbers, and hyphens';
        }
        break;
        
      case 'description':
        // if (!value || !value.trim()) {
        //   error = 'Description is required';
        // } else if (value.trim().length < 10) {
        //   error = 'Description must be at least 10 characters';
        // } else if (value.trim().length > 2000) {
        //   error = 'Description must be less than 2000 characters';
        // }
        break;
        
      case 'meta_title':
        if (value && value.length > 60) {
          error = 'Meta title should be less than 60 characters for SEO';
        }
        break;
        
      case 'meta_description':
        if (value && value.length > 160) {
          error = 'Meta description should be less than 160 characters for SEO';
        }
        break;
        
      case 'display_order':
        if (value < 0) {
          error = 'Display order cannot be negative';
        } else if (value > 999) {
          error = 'Display order must be less than 1000';
        }
        break;
        
      default:
        break;
    }
    
    return error;
  };

  // Validation message component
  const ValidationMessage = ({ error, type = 'error' }) => {
    if (!error) return null;
    
    const styles = {
      error: 'bg-gradient-to-r from-red-50 to-pink-50 border-red-200 text-red-700',
      warning: 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 text-amber-700',
      success: 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 text-green-700'
    };
    
    const icons = {
      error: '❌',
      warning: '⚠️',
      success: '✅'
    };
    
    return (
      <div className={`${styles[type]} border px-3 py-2 rounded-lg text-sm font-medium mt-1 shadow-sm animate-fade-in`}>
        <div className="flex items-center">
          <span className="mr-2">{icons[type]}</span>
          {error}
        </div>
      </div>
    );
  };

  // Check form validity
  const checkDestinationFormValidity = (errors, data) => {
    const hasErrors = Object.values(errors).some(err => err !== '');
    const hasRequiredFields = data.name.trim() && data.slug.trim();
    setIsDestinationFormValid(!hasErrors && hasRequiredFields);
  };

  const checkSubDestinationFormValidity = (errors, data) => {
    const hasErrors = Object.values(errors).some(err => err !== '');
    const hasRequiredFields = data.name.trim() && data.slug.trim();
    setIsSubDestinationFormValid(!hasErrors && hasRequiredFields);
  };

  useEffect(() => {
    fetchDestinations();
  }, []);

  const fetchDestinations = async () => {
    // Prevent duplicate calls
    if (isFetching) {
      console.log('🚫 Fetch already in progress, skipping duplicate call');
      return;
    }

    setIsFetching(true);

    // Check cache first
    const cached = getCachedData();
    if (cached && Array.isArray(cached) && cached.length > 0) {
      console.log('📋 Using cached destinations:', cached.length, 'items');
      setDestinations(cached);
      setCacheStatus('cached');
      setLoading(false);
      setIsFetching(false);
      return;
    }

    // Fetch fresh data
    setCacheStatus('loading');
    setLoading(true);
    try {
      console.log('🔄 Fetching fresh destinations from API...');
      const response = await getDestinations();
      console.log('API Response:', response);
      
      // Handle all possible response structures
      let destinationsData = [];
      
      if (Array.isArray(response)) {
        // Direct array response
        destinationsData = response;
      } else if (response && Array.isArray(response.data)) {
        // Response.data is array
        destinationsData = response.data;
      } else if (response && response.data && Array.isArray(response.data.data)) {
        // Nested response.data.data structure
        destinationsData = response.data.data;
      } else if (response && response.data && typeof response.data === 'object') {
        // Single object response
        destinationsData = [response.data];
      } else {
        console.warn('Unexpected response structure:', response);
        destinationsData = [];
      }
      
      console.log('Extracted destinations data:', destinationsData);
      
      if (destinationsData.length === 0) {
        console.warn('No destinations found in response');
        setDestinations([]);
        setCacheStatus('fresh');
        return;
      }
      
      // Fetch sub_destinations for each destination
      console.log('Fetching sub-destinations for each destination...');
      const destinationsWithSubs = await Promise.all(
        destinationsData.map(async (dest) => {
          try {
            console.log(`Fetching sub-destinations for destination: ${dest.name} (ID: ${dest.id})`);
            const subDestResponse = await getSubDestinationsByDestination(dest.id);
            
            // Handle sub-destinations response structure
            let subDestinations = [];
            
            if (Array.isArray(subDestResponse)) {
              subDestinations = subDestResponse;
            } else if (subDestResponse && Array.isArray(subDestResponse.data)) {
              subDestinations = subDestResponse.data;
            } else if (subDestResponse && subDestResponse.data && Array.isArray(subDestResponse.data.data)) {
              subDestinations = subDestResponse.data.data;
            } else {
              subDestinations = [];
            }
            
            return {
              ...dest,
              sub_destinations: subDestinations
            };
          } catch (error) {
            console.error(`Failed to fetch sub_destinations for ${dest.name} (${dest.id}):`, error);
            return {
              ...dest,
              sub_destinations: []
            };
          }
        })
      );
      
      console.log('✅ Destinations loaded and cached:', destinationsWithSubs.length, 'items');
      setDestinations(destinationsWithSubs);
      setCachedData(destinationsWithSubs);
      setCacheStatus('fresh');
    } catch (err) {
      console.error('Fetch destinations error:', err);
      setError('Failed to load locations: ' + err.message);
      setCacheStatus('error');
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const handleDestinationFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setDestinationForm(prev => ({
      ...prev,
      [name]: newValue,
      ...(name === 'name' && { slug: slugify(value) })
    }));
    
    // Real-time validation for non-checkbox fields
    if (type !== 'checkbox') {
      let error = '';
      if (name === 'name') {
        // Validate both name and generated slug
        const nameError = validateField('name', newValue);
        const slugError = validateField('slug', slugify(newValue));
        error = nameError || slugError;
        
        setDestinationValidationErrors(prev => ({
          ...prev,
          name: nameError,
          slug: slugError
        }));
      } else {
        error = validateField(name, newValue);
        setDestinationValidationErrors(prev => ({
          ...prev,
          [name]: error
        }));
      }
      
      // Check form validity
      const newFormData = { 
        ...destinationForm, 
        [name]: newValue,
        ...(name === 'name' && { slug: slugify(newValue) })
      };
      const newErrors = { 
        ...destinationValidationErrors, 
        [name]: error,
        ...(name === 'name' && { slug: validateField('slug', slugify(newValue)) })
      };
      checkDestinationFormValidity(newErrors, newFormData);
    }
  };

  const handleDestinationDescriptionChange = (value) => {
    setDestinationForm(prev => ({ ...prev, description: value }));
    
    // Validate description
    const error = validateField('description', value);
    setDestinationValidationErrors(prev => ({
      ...prev,
      description: error
    }));
    
    // Check form validity
    const newFormData = { ...destinationForm, description: value };
    const newErrors = { ...destinationValidationErrors, description: error };
    checkDestinationFormValidity(newErrors, newFormData);
  };

  const handleSubDestinationFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setSubDestinationForm(prev => ({
      ...prev,
      [name]: newValue,
      ...(name === 'name' && { slug: slugify(value) })
    }));
    
    // Real-time validation for non-checkbox fields
    if (type !== 'checkbox') {
      let error = '';
      if (name === 'name') {
        // Validate both name and generated slug
        const nameError = validateField('name', newValue);
        const slugError = validateField('slug', slugify(newValue));
        error = nameError || slugError;
        
        setSubDestinationValidationErrors(prev => ({
          ...prev,
          name: nameError,
          slug: slugError
        }));
      } else {
        error = validateField(name, newValue);
        setSubDestinationValidationErrors(prev => ({
          ...prev,
          [name]: error
        }));
      }
      
      // Check form validity
      const newFormData = { 
        ...subDestinationForm, 
        [name]: newValue,
        ...(name === 'name' && { slug: slugify(newValue) })
      };
      const newErrors = { 
        ...subDestinationValidationErrors, 
        [name]: error,
        ...(name === 'name' && { slug: validateField('slug', slugify(newValue)) })
      };
      checkSubDestinationFormValidity(newErrors, newFormData);
    }
  };

  const handleSubDestinationDescriptionChange = (value) => {
    setSubDestinationForm(prev => ({ ...prev, description: value }));
    
    // Validate description
    const error = validateField('description', value);
    setSubDestinationValidationErrors(prev => ({
      ...prev,
      description: error
    }));
    
    // Check form validity
    const newFormData = { ...subDestinationForm, description: value };
    const newErrors = { ...subDestinationValidationErrors, description: error };
    checkSubDestinationFormValidity(newErrors, newFormData);
  };

  const handleSaveDestination = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (destinationForm.id) {
        const updatedDestination = await updateDestination(destinationForm.id, destinationForm);
        updateDestinationInCache(updatedDestination);
        showSuccess('Destination updated successfully!');
      } else {
        const newDestination = await createDestination(destinationForm);
        addDestinationToCache(newDestination);
        showSuccess('Destination created successfully!');
      }
      resetDestinationForm();
      setShowAddDestinationForm(false);
    } catch (err) {
      setError('Failed to save destination: ' + err.message);
      showError('Failed to save destination: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDestination = async (id) => {
    if (window.confirm('Are you sure you want to delete this destination and all its sub-destinations?')) {
      setLoading(true);
      setError('');
      try {
        await deleteDestination(id);
        removeDestinationFromCache(id);
        setShowSubDestinationFormForDest(null);
        showSuccess('Destination deleted successfully!');
      } catch (err) {
        setError('Failed to delete destination: ' + err.message);
        showError('Failed to delete destination: ' + err.message);
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSaveSubDestination = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = { ...subDestinationForm, destination_id: showSubDestinationFormForDest };
      if (editingSubDestinationId) {
        const updatedSubDestination = await updateSubDestination(editingSubDestinationId, payload);
        updateSubDestinationInCache(showSubDestinationFormForDest, updatedSubDestination);
        showSuccess('Sub-destination updated successfully!');
      } else {
        const newSubDestination = await createSubDestination(payload);
        addSubDestinationToCache(showSubDestinationFormForDest, newSubDestination);
        showSuccess('Sub-destination created successfully!');
      }
      resetSubDestinationForm();
      setEditingSubDestinationId(null);
      setShowSubDestinationFormForDest(null); 
    } catch (err) {
      setError('Failed to save sub-destination: ' + err.message);
      showError('Failed to save sub-destination: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubDestination = async (id) => {
    if (window.confirm('Are you sure you want to delete this sub-destination?')) {
      setLoading(true);
      setError('');
      try {
        await deleteSubDestination(id);
        removeSubDestinationFromCache(id);
        showSuccess('Sub-destination deleted successfully!');
      } catch (err) {
        setError('Failed to delete sub-destination: ' + err.message);
        showError('Failed to delete sub-destination: ' + err.message);
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const resetDestinationForm = () => {
    setDestinationForm({
      id: '', name: '', slug: '', description: '', meta_title: '', meta_description: '', is_active: true, display_order: 0
    });
  };

  const resetSubDestinationForm = () => {
    setSubDestinationForm({
      id: '', name: '', slug: '', description: '', is_active: true, display_order: 0, destination_id: null
    });
  };

  const handleEditDestinationClick = (dest) => {
    setDestinationForm(dest);
    setShowAddDestinationForm(true);
    setShowSubDestinationFormForDest(null); 
    setEditingSubDestinationId(null);
    resetSubDestinationForm();
  };

  const handleToggleSubDestinationForm = (destinationId) => {
    if (showSubDestinationFormForDest === destinationId) {
      setShowSubDestinationFormForDest(null);
      setEditingSubDestinationId(null);
      resetSubDestinationForm();
    } else {
      setShowSubDestinationFormForDest(destinationId);
      setEditingSubDestinationId(null); 
      resetSubDestinationForm();
    }
  };

  const handleEditSubDestinationClick = (subDest, parentDestinationId) => {
    setSubDestinationForm(subDest);
    setEditingSubDestinationId(subDest.id);
    setShowSubDestinationFormForDest(parentDestinationId);
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm mx-0.5 my-0.5" role="alert"> 
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <div className="w-3 h-3 bg-indigo-500 rounded-full mr-3"></div>
          Destination Management
        </h2>
        <div className="flex items-center space-x-4">
          <CacheStatusIndicator status={cacheStatus} itemCount={destinations.length} />
          <button
            type="button"
            onClick={refreshDestinations}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh destinations data"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>🔄 Refresh Data</span>
          </button>
        </div>
      </div> 

      {/* Add/Edit Destination Form Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300"> 
        <div className="flex justify-between items-center mb-4"> 
          <h3 className="text-lg font-semibold text-gray-900 flex items-center"> 
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
            {destinationForm.id ? 'Edit Destination' : 'Add New Destination'}
          </h3>
          {!showAddDestinationForm && (
            <Button
              onClick={() => {
                resetDestinationForm();
                setShowAddDestinationForm(true);
                setShowSubDestinationFormForDest(null); 
                setEditingSubDestinationId(null);
              }}
              variant="primary"
              className="inline-flex items-center py-2 px-4 text-sm font-medium rounded-lg hover:shadow-md transition-all duration-200" 
            >
              <PlusCircleIcon className="h-4 w-4 mr-2" /> Add New Destination
            </Button>
          )}
        </div>

        {showAddDestinationForm && (
          <form onSubmit={handleSaveDestination} className="grid grid-cols-1 md:grid-cols-2 gap-4"> 
            <div>
              <Input 
                label="Name" 
                id="dest-name" 
                name="name" 
                value={destinationForm.name} 
                onChange={handleDestinationFormChange} 
                required 
                className={`text-sm ${destinationValidationErrors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-blue-200 focus:border-blue-500 focus:ring-blue-500'}`}
                placeholder="Enter destination name"
              />
              <ValidationMessage error={destinationValidationErrors.name} />
              {destinationForm.name && !destinationValidationErrors.name && destinationForm.name.trim().length >= 2 && (
                <ValidationMessage error={`Great name! (${destinationForm.name.trim().length} characters)`} type="success" />
              )}
            </div>
            
            <div>
              <Input 
                label="Slug" 
                id="dest-slug" 
                name="slug" 
                value={destinationForm.slug} 
                onChange={handleDestinationFormChange} 
                disabled={!!destinationForm.id} 
                helpText="Auto-generated from name" 
                className={`text-sm ${destinationValidationErrors.slug ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-blue-200 focus:border-blue-500 focus:ring-blue-500'}`}
              />
              <ValidationMessage error={destinationValidationErrors.slug} />
              {destinationForm.slug && !destinationValidationErrors.slug && destinationForm.slug.length >= 2 && (
                <ValidationMessage error="Valid URL slug format" type="success" />
              )}
            </div>
            
            {/* <div className="md:col-span-2"> 
              <RichTextEditor 
                label="Description" 
                value={destinationForm.description} 
                onChange={handleDestinationDescriptionChange} 
                className={`text-sm ${destinationValidationErrors.description ? 'border-red-300' : 'border-blue-200'}`}
                placeholder="Enter detailed destination description (min 10 characters)"
              />
              <ValidationMessage error={destinationValidationErrors.description} />
              {destinationForm.description && !destinationValidationErrors.description && destinationForm.description.trim().length >= 10 && (
                <ValidationMessage error={`Perfect description! (${destinationForm.description.trim().length} characters)`} type="success" />
              )}
              {destinationForm.description && destinationForm.description.trim().length > 0 && destinationForm.description.trim().length < 10 && (
                <ValidationMessage error={`${10 - destinationForm.description.trim().length} more characters needed`} type="warning" />
              )}
            </div> */}
            
            <div>
              <Input 
                label="Meta Title" 
                id="dest-meta-title" 
                name="meta_title" 
                value={destinationForm.meta_title} 
                onChange={handleDestinationFormChange} 
                className={`text-sm ${destinationValidationErrors.meta_title ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-green-200 focus:border-green-500 focus:ring-green-500'}`}
                placeholder="SEO meta title (optional)"
              />
              <ValidationMessage error={destinationValidationErrors.meta_title} />
              {destinationForm.meta_title && destinationForm.meta_title.length <= 60 && destinationForm.meta_title.length > 0 && (
                <ValidationMessage error={`Good SEO length (${destinationForm.meta_title.length}/60 chars)`} type="success" />
              )}
              {destinationForm.meta_title && destinationForm.meta_title.length > 50 && destinationForm.meta_title.length <= 60 && (
                <ValidationMessage error={`${60 - destinationForm.meta_title.length} characters left for SEO`} type="warning" />
              )}
            </div>
            
            <div>
              <Input 
                label="Meta Description" 
                id="dest-meta-description" 
                name="meta_description" 
                value={destinationForm.meta_description} 
                onChange={handleDestinationFormChange} 
                className={`text-sm ${destinationValidationErrors.meta_description ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-green-200 focus:border-green-500 focus:ring-green-500'}`}
                placeholder="SEO meta description (optional)"
              />
              <ValidationMessage error={destinationValidationErrors.meta_description} />
              {destinationForm.meta_description && destinationForm.meta_description.length <= 160 && destinationForm.meta_description.length > 0 && (
                <ValidationMessage error={`Good SEO length (${destinationForm.meta_description.length}/160 chars)`} type="success" />
              )}
              {destinationForm.meta_description && destinationForm.meta_description.length > 140 && destinationForm.meta_description.length <= 160 && (
                <ValidationMessage error={`${160 - destinationForm.meta_description.length} characters left for SEO`} type="warning" />
              )}
            </div>
            
            <div>
              <Input 
                label="Display Order" 
                id="dest-display-order" 
                name="display_order" 
                type="number" 
                value={destinationForm.display_order} 
                onChange={handleDestinationFormChange} 
                className={`text-sm ${destinationValidationErrors.display_order ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-amber-200 focus:border-amber-500 focus:ring-amber-500'}`}
                min="0"
                max="999"
              />
              <ValidationMessage error={destinationValidationErrors.display_order} />
              {destinationForm.display_order >= 0 && destinationForm.display_order <= 999 && !destinationValidationErrors.display_order && (
                <ValidationMessage error={`Order: ${destinationForm.display_order} ${destinationForm.display_order === 0 ? '(appears first)' : ''}`} type="success" />
              )}
            </div>
            
            <div className="flex items-center py-2"> 
              <input type="checkbox" id="dest-is-active" name="is_active" checked={destinationForm.is_active} onChange={handleDestinationFormChange} className="form-checkbox h-4 w-4 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500" />
              <label htmlFor="dest-is-active" className="ml-3 text-sm text-gray-700 font-medium flex items-center">
                Is Active
                {destinationForm.is_active && <div className="w-2 h-2 bg-green-500 rounded-full ml-2"></div>}
              </label>
            </div>
            
            <div className="md:col-span-2 flex justify-end space-x-3 mt-4 pt-4 border-t border-gray-200"> 
              <Button type="button" onClick={() => { setShowAddDestinationForm(false); resetDestinationForm(); setShowSubDestinationFormForDest(null); setDestinationValidationErrors({}); }} variant="secondary" className="py-2 px-4 text-sm font-medium rounded-lg"> 
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="success" 
                disabled={loading || !isDestinationFormValid || Object.values(destinationValidationErrors).some(err => err !== '')}
                className={`py-2 px-4 text-sm font-medium rounded-lg hover:shadow-md transition-all duration-200 ${
                  (!isDestinationFormValid || Object.values(destinationValidationErrors).some(err => err !== '')) 
                    ? 'opacity-50 cursor-not-allowed' 
                    : ''
                }`}
              > 
                {destinationForm.id ? 'Update' : 'Add'} Destination
              </Button>
            </div>
          </form>
        )}
      </div>

      {/* Destinations List */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300"> 
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
          Existing Destinations
        </h3> 
        {destinations.length === 0 && !loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="text-gray-500 text-sm">No destinations found. Add one using the form above.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {destinations.map(dest => (
              <div key={dest.id} className="border border-gray-200 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200">
                {/* Destination Header */}
                <div className="flex justify-between items-center px-6 py-4 bg-white border-b border-gray-200"> 
                  <div className="flex-1 min-w-0 pr-4"> 
                    <div className="flex items-center mb-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                      <h4 className="text-lg font-semibold text-gray-900 truncate">{dest.name}</h4> 
                    </div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{dest.description?.substring(0, 150)}{dest.description?.length > 150 ? '...' : ''}</p>
                    <div className="flex items-center space-x-4"> 
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            dest.is_active ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'
                        }`}>
                            {dest.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <span className="text-sm text-gray-600">Display Order: <span className="font-medium text-gray-900">{dest.display_order}</span></span>
                    </div>
                  </div>
                  <div className="flex-shrink-0 flex items-center space-x-2"> 
                    <Button onClick={() => handleEditDestinationClick(dest)} variant="secondary" className="p-2 text-indigo-600 hover:text-white hover:bg-indigo-600 rounded-lg transition-colors duration-200">
                      <PencilIcon className="h-4 w-4" /> 
                    </Button>
                    <Button onClick={() => handleDeleteDestination(dest.id)} variant="secondary" className="p-2 text-red-600 hover:text-white hover:bg-red-600 rounded-lg transition-colors duration-200">
                      <TrashIcon className="h-4 w-4" /> 
                    </Button>
                  </div>
                </div>
                
                {/* Sub-destinations Section */}
                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-t border-orange-200 p-6"> 
                    <div className="flex justify-between items-center mb-4"> 
                        <h5 className="text-md font-semibold text-gray-800 flex items-center">
                          <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                          Sub-destinations ({dest.sub_destinations.length})
                        </h5> 
                        <Button 
                            onClick={() => handleToggleSubDestinationForm(dest.id)} 
                            variant="primary" 
                            className="py-2 px-4 text-sm font-medium inline-flex items-center rounded-lg hover:shadow-md transition-all duration-200" 
                        >
                            {showSubDestinationFormForDest === dest.id && editingSubDestinationId === null ? (
                                <>Cancel <XMarkIcon className="ml-2 h-4 w-4" /></> 
                            ) : (
                                <>Add Sub-destination <PlusCircleIcon className="ml-2 h-4 w-4" /></> 
                            )}
                        </Button>
                    </div>

                    {/* Sub-destination Add/Edit Form */}
                    {(showSubDestinationFormForDest === dest.id) && (
                        <div className="mb-4 p-4 border-2 border-green-300 rounded-lg bg-white shadow-sm"> 
                            <h6 className="text-sm font-semibold text-green-800 mb-3 flex items-center"> 
                                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                                {editingSubDestinationId ? 'Edit Sub-destination' : 'Add New Sub-destination'}
                            </h6>
                            <form onSubmit={handleSaveSubDestination} className="grid grid-cols-1 md:grid-cols-2 gap-4"> 
                                <div>
                                    <Input 
                                        label="Name" 
                                        id="sub-name" 
                                        name="name" 
                                        value={subDestinationForm.name} 
                                        onChange={handleSubDestinationFormChange} 
                                        required 
                                        className={`text-sm ${subDestinationValidationErrors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-blue-200 focus:border-blue-500 focus:ring-blue-500'}`}
                                        placeholder="Enter sub-destination name"
                                    />
                                    <ValidationMessage error={subDestinationValidationErrors.name} />
                                    {subDestinationForm.name && !subDestinationValidationErrors.name && subDestinationForm.name.trim().length >= 2 && (
                                        <ValidationMessage error={`Great name! (${subDestinationForm.name.trim().length} characters)`} type="success" />
                                    )}
                                </div>
                                
                                <div>
                                    <Input 
                                        label="Slug" 
                                        id="sub-slug" 
                                        name="slug" 
                                        value={subDestinationForm.slug} 
                                        onChange={handleSubDestinationFormChange} 
                                        disabled={!!editingSubDestinationId} 
                                        helpText="Auto-generated from name" 
                                        className={`text-sm ${subDestinationValidationErrors.slug ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-blue-200 focus:border-blue-500 focus:ring-blue-500'}`}
                                    />
                                    <ValidationMessage error={subDestinationValidationErrors.slug} />
                                    {subDestinationForm.slug && !subDestinationValidationErrors.slug && subDestinationForm.slug.length >= 2 && (
                                        <ValidationMessage error="Valid URL slug format" type="success" />
                                    )}
                                </div>
                                
                                {/* <div className="md:col-span-2"> 
                                    <RichTextEditor 
                                        label="Description" 
                                        value={subDestinationForm.description} 
                                        onChange={handleSubDestinationDescriptionChange} 
                                        className={`text-sm ${subDestinationValidationErrors.description ? 'border-red-300' : 'border-blue-200'}`}
                                        placeholder="Enter detailed sub-destination description (min 10 characters)"
                                    />
                                    <ValidationMessage error={subDestinationValidationErrors.description} />
                                    {subDestinationForm.description && !subDestinationValidationErrors.description && subDestinationForm.description.trim().length >= 10 && (
                                        <ValidationMessage error={`Perfect description! (${subDestinationForm.description.trim().length} characters)`} type="success" />
                                    )}
                                    {subDestinationForm.description && subDestinationForm.description.trim().length > 0 && subDestinationForm.description.trim().length < 10 && (
                                        <ValidationMessage error={`${10 - subDestinationForm.description.trim().length} more characters needed`} type="warning" />
                                    )}
                                </div> */}
                                
                                <div>
                                    <Input 
                                        label="Display Order" 
                                        id="sub-display-order" 
                                        name="display_order" 
                                        type="number" 
                                        value={subDestinationForm.display_order} 
                                        onChange={handleSubDestinationFormChange} 
                                        className={`text-sm ${subDestinationValidationErrors.display_order ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-amber-200 focus:border-amber-500 focus:ring-amber-500'}`}
                                        min="0"
                                        max="999"
                                    />
                                    <ValidationMessage error={subDestinationValidationErrors.display_order} />
                                    {subDestinationForm.display_order >= 0 && subDestinationForm.display_order <= 999 && !subDestinationValidationErrors.display_order && (
                                        <ValidationMessage error={`Order: ${subDestinationForm.display_order} ${subDestinationForm.display_order === 0 ? '(appears first)' : ''}`} type="success" />
                                    )}
                                </div>
                                
                                <div className="flex items-center py-2"> 
                                    <input type="checkbox" id="sub-is-active" name="is_active" checked={subDestinationForm.is_active} onChange={handleSubDestinationFormChange} className="form-checkbox h-4 w-4 text-green-600 rounded focus:ring-2 focus:ring-green-500" /> 
                                    <label htmlFor="sub-is-active" className="ml-3 text-sm text-gray-700 font-medium flex items-center">
                                        Is Active
                                        {subDestinationForm.is_active && <div className="w-2 h-2 bg-green-500 rounded-full ml-2"></div>}
                                    </label>
                                </div>
                                
                                <div className="md:col-span-2 flex justify-end space-x-3 mt-4 pt-4 border-t border-gray-200"> 
                                    <Button type="button" onClick={() => { setShowSubDestinationFormForDest(null); setEditingSubDestinationId(null); resetSubDestinationForm(); setSubDestinationValidationErrors({}); }} variant="secondary" className="py-2 px-4 text-sm font-medium rounded-lg"> 
                                        Cancel
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        variant="success" 
                                        disabled={loading || !isSubDestinationFormValid || Object.values(subDestinationValidationErrors).some(err => err !== '')}
                                        className={`py-2 px-4 text-sm font-medium rounded-lg hover:shadow-md transition-all duration-200 ${
                                            (!isSubDestinationFormValid || Object.values(subDestinationValidationErrors).some(err => err !== '')) 
                                                ? 'opacity-50 cursor-not-allowed' 
                                                : ''
                                        }`}
                                    > 
                                        {editingSubDestinationId ? 'Update' : 'Add'} Sub-destination
                                    </Button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Sub-destinations List */}
                    {dest.sub_destinations.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <svg className="w-6 h-6 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                        </div>
                        <p className="text-gray-500 text-sm">No sub-destinations for {dest.name}</p>
                      </div>
                    ) : (
                      <div className="rounded-lg border border-orange-200 overflow-hidden bg-white shadow-sm">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-orange-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 uppercase tracking-wider">Name</th> 
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 uppercase tracking-wider">Status</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 uppercase tracking-wider">Display Order</th>
                              <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {(dest.sub_destinations || []).map(subDest => (
                              <tr key={subDest.id} className="hover:bg-orange-25 transition-colors duration-150">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900 truncate max-w-0">
                                  <div className="flex items-center">
                                    <div className="w-1.5 h-1.5 bg-orange-400 rounded-full mr-3"></div>
                                    {subDest.name}
                                  </div>
                                </td> 
                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                  {subDest.is_active ? (
                                    <span className="px-2 py-1 text-sm font-medium rounded-full bg-green-100 text-green-800 border border-green-200">Active</span> 
                                  ) : (
                                    <span className="px-2 py-1 text-sm font-medium rounded-full bg-red-100 text-red-800 border border-red-200">Inactive</span> 
                                  )}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 font-medium">{subDest.display_order}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium space-x-2"> 
                                  <Button onClick={() => handleEditSubDestinationClick(subDest, dest.id)} variant="secondary" className="p-2 text-indigo-600 hover:text-white hover:bg-indigo-600 rounded-lg transition-colors duration-200">
                                    <PencilIcon className="h-4 w-4" />
                                  </Button>
                                  <Button onClick={() => handleDeleteSubDestination(subDest.id)} variant="secondary" className="p-2 text-red-600 hover:text-white hover:bg-red-600 rounded-lg transition-colors duration-200">
                                    <TrashIcon className="h-4 w-4" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default LocationManagementPage;
